<template>
  <div class="signin-page-shell">
    <main class="signin-page-main">
      <div class="signin-page-card">
        <BrandLogo class="signin-page-logo" />
        <h1 class="signin-page-heading">{{ invitation ? $t('invitation.headingSignIn') : $t('signin.title') }}</h1>
        <InvitationBanner :preview="invitation" :dead="invitationDead" />

        <button
          type="button"
          class="signin-page-google"
          :disabled="googleLoading || loading"
          @click="signInWithGoogle"
        >
          <svg class="signin-page-google__icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.164 6.656 3.58 9 3.58Z" fill="#EA4335"/>
          </svg>
          <span>{{ googleLoading ? $t('signin.googleLoading') : $t('signin.google') }}</span>
        </button>

        <div class="signin-page-divider">
          <span>{{ $t('signin.or') }}</span>
        </div>

        <v-form ref="form" v-model="valid" @submit.prevent="onSubmit">
          <v-text-field
            v-model="email"
            type="email"
            :label="$t('signin.email')"
            outlined
            dense
            persistent-placeholder
            autocomplete="username"
            :rules="emailRules"
            class="signin-page-input"
            hide-details="auto"
            data-test="signin-email"
          />

          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :label="$t('signin.password')"
            outlined
            dense
            persistent-placeholder
            autocomplete="current-password"
            :rules="passwordRules"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            class="signin-page-input"
            hide-details="auto"
            data-test="signin-password"
            @click:append="showPassword = !showPassword"
          />

          <div class="signin-page-forgot">
            <router-link to="/forgot-password" class="signin-page-link signin-page-link--muted">{{ $t('signin.forgot') }}</router-link>
          </div>

          <TurnstileWidget ref="turnstile" @token="captchaToken = $event" />

          <p v-if="errorMsg" class="signin-page-error" role="alert">{{ errorMsg }}</p>

          <v-btn
            type="submit"
            block
            depressed
            large
            :loading="loading"
            :disabled="!valid || googleLoading || esperandoCaptcha"
            class="signin-page-submit"
          >
            {{ $t('signin.submit') }}
          </v-btn>
        </v-form>

        <p v-if="invitationToken" class="signin-page-footer">
          {{ $t('signin.invited') }}
          <router-link :to="signUpPath" class="signin-page-link">
            {{ $t('signin.createAccount') }}
            <span aria-hidden="true" class="signin-page-arrow">↗</span>
          </router-link>
        </p>
        <p v-else class="signin-page-footer">
          {{ $t('signin.noAccess') }}
          <a :href="contactUrl" class="signin-page-link" rel="noopener" data-test="signin-request-access">
            {{ $t('signin.requestAccess') }}
            <span aria-hidden="true" class="signin-page-arrow">↗</span>
          </a>
        </p>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { supabase } from '@/services/supabase';
import { okeyTrack } from '@/utils/track';
import { setAccessToken } from '@/services/funnelApi';
import { api, createHandoffCode, buildHandoffUrl } from '@/services/api';
import { estadoHttp, segundosDeEspera, tipoDeNegativa } from '@/services/apiErrors';
import { turnstileEncendido } from '@/services/turnstile';
import InvitationBanner from '@/invitations/InvitationBanner.vue';
import { invitationLanding, invitationTokenFromUrl, previewInvitation, withInvitationInApp, type InvitationPreview } from '@/invitations/invitation';
import { paramDeLaUrl } from '@/utils/urlParams';
import { CONTACT_URL } from '@/utils/contact';
import BrandLogo from '@/components/brand/BrandLogo.vue';
import TurnstileWidget from '@/components/TurnstileWidget.vue';

const MAIN_APP_URL = process.env.VUE_APP_MAIN_APP_URL || 'http://localhost:8080';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Lo que devuelve `POST /api/auth/acceso` (contrato de NOTAS-acceso.md). */
interface SesionDeAcceso {
  access_token: string;
  refresh_token: string;
}

export default defineComponent({
  name: 'SignInView',
  components: { BrandLogo, InvitationBanner, TurnstileWidget },
  data() {
    return {
      email: '' as string,
      invitationToken: null as string | null,
      invitation: null as InvitationPreview | null,
      invitationDead: false as boolean,
      password: '' as string,
      valid: false as boolean,
      loading: false as boolean,
      googleLoading: false as boolean,
      showPassword: false as boolean,
      errorMsg: '' as string,
      captchaToken: null as string | null,
      contactUrl: CONTACT_URL,
      emailRules: [
        (v: string): boolean | string => !!v || String(this.$t('signin.emailRequired')),
        (v: string): boolean | string => EMAIL_RE.test(v) || String(this.$t('signin.emailInvalid')),
      ],
      passwordRules: [
        (v: string): boolean | string => !!v || String(this.$t('signin.passwordRequired')),
      ],
    };
  },
  computed: {
    signUpPath(): string {
      return withInvitationInApp('/signup', this.invitationToken);
    },
    /** Con Turnstile encendido, el botón espera a tener un token. */
    esperandoCaptcha(): boolean {
      return turnstileEncendido && !this.captchaToken;
    },
  },
  async mounted(): Promise<void> {
    try {
      const url = new URL(window.location.href);
      const prefill = paramDeLaUrl('email');
      if (prefill) this.email = prefill;
      this.invitationToken = invitationTokenFromUrl();
      if (this.invitationToken) {
        this.invitation = await previewInvitation(this.invitationToken);
        if (this.invitation) this.email = this.invitation.email;
        else this.invitationDead = true;
      }
      // Distingue "llegó al acceso" de "llegó al alta": si el tráfico nuevo
      // sigue aterrizando aquí, es que algún CTA vuelve a apuntar al login.
      okeyTrack('auth_page_view', {
        screen: 'signin',
        plan_interest: url.searchParams.get('plan_interest') || 'none',
        prefilled: !!prefill,
      });
    } catch {
      // ignore
    }
  },
  methods: {
    /**
     * El acceso con contraseña pasa por el backend (punto 10): es el único sitio
     * que limita intentos por IP y por email, verifica Turnstile, contesta lo
     * MISMO exista o no la cuenta y deja rastro en el registro de accesos.
     * Con la sesión que devuelve se sigue el handoff de siempre.
     */
    async onSubmit(): Promise<void> {
      this.errorMsg = '';
      const valid = (this.$refs.form as unknown as { validate(): boolean }).validate();
      if (!valid || this.esperandoCaptcha) return;
      this.loading = true;
      try {
        const { data } = await api.post<SesionDeAcceso>('/api/auth/acceso', {
          email: this.email.trim(),
          password: this.password,
          ...(this.captchaToken ? { captcha_token: this.captchaToken } : {}),
        });
        if (!data?.access_token || !data?.refresh_token) throw new Error('acceso: respuesta sin sesión');

        const { data: sesion, error } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        if (error) throw new Error('acceso: setSession rechazó la sesión');
        const accessToken = sesion.session?.access_token || data.access_token;
        const refreshToken = sesion.session?.refresh_token || data.refresh_token;

        try { setAccessToken(accessToken); } catch { /* ignore */ }

        // Handoff al main app mediante código de un solo uso: los tokens no
        // viajan por la URL, donde los ven logs, historial, Referer y analítica.
        const handoffCode = await createHandoffCode(accessToken, refreshToken);
        if (!handoffCode) throw new Error('acceso: sin código de handoff');
        // Con una invitación en la mano se aterriza en su aceptación.
        const destino = this.invitationToken ? invitationLanding(this.invitationToken) : '/inbox';
        window.location.href = buildHandoffUrl(MAIN_APP_URL, handoffCode, destino);
      } catch (err) {
        this.errorMsg = this.mensajeDeError(err);
      } finally {
        this.loading = false;
        // El token de Turnstile ya se gastó: el siguiente intento necesita otro.
        (this.$refs.turnstile as unknown as { reset?: () => void } | undefined)?.reset?.();
      }
    },
    /**
     * Siempre una frase de i18n; nunca el texto que trae la negativa. Un fallo
     * sin respuesta HTTP (red, setSession, handoff) cae en la genérica.
     */
    mensajeDeError(err: unknown): string {
      const status = estadoHttp(err);
      const tipo = tipoDeNegativa(err);
      if (status === 401 || tipo === 'CREDENCIALES') return String(this.$t('signin.errors.credentials'));
      if (status === 429 || tipo === 'DEMASIADOS_INTENTOS') {
        const segundos = segundosDeEspera(err);
        return segundos
          ? String(this.$t('signin.errors.tooManySeconds', { seconds: segundos }))
          : String(this.$t('signin.errors.tooMany'));
      }
      if (tipo === 'CAPTCHA') return String(this.$t('signin.errors.captcha'));
      return String(this.$t('signin.errors.generic'));
    },
    /**
     * Google sign-in is started by the MAIN APP, not here.
     *
     * PKCE only works when the origin that redeems the ?code= is the one that
     * generated the code_verifier. Starting the flow on this origin meant the
     * session was minted here and then shipped to app.ininvoice.com in the
     * query string — which is how access and refresh tokens reached nginx
     * access logs. Handing control over first costs one plain redirect that
     * carries no secret at all.
     */
    signInWithGoogle(): void {
      this.errorMsg = '';
      this.googleLoading = true;
      // Se emite ANTES de saltar porque el redirect abandona la página.
      okeyTrack('signup_started', { method: 'google' });
      window.location.href = `${MAIN_APP_URL}/auth/google`;
    },
  },
});
</script>

<style scoped>
.signin-page-shell {
  --ink:        var(--ii-ink);
  --ink-hushed: var(--ii-ink-2);
  --border:     var(--ii-rule);
  min-height: 100vh;
  background: var(--casa-fondo);
  color: var(--ink);
  font-family: var(--ii-display);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
}

.signin-page-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--casa-espacio-8) var(--casa-espacio-6) 64px;
}

.signin-page-card {
  width: 100%;
  max-width: 460px;
}

.signin-page-logo { margin-bottom: var(--casa-espacio-2); }

.signin-page-heading {
  margin: 0 0 28px;
  font-family: inherit;
  font-size: 40px;
  line-height: 1.0;
  font-weight: 500;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;
}
@media (min-width: 768px) {
  .signin-page-heading { font-size: 48px; }
}

.signin-page-google {
  width: 100%;
  height: var(--casa-alto-fila);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--casa-espacio-2-5);
  background: var(--casa-fondo);
  color: var(--ink);
  font-size: var(--casa-letra-seccion);
  font-weight: 400;
  border: 1px solid var(--border);
  border-radius: var(--casa-radio-tarjeta);
  cursor: pointer;
  font-family: inherit;
  transition: background .15s, border-color .15s;
}
.signin-page-google:hover {
  background: var(--casa-fondo-app);
  border-color: var(--ink);
}
.signin-page-google:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.signin-page-google__icon { flex-shrink: 0; }

.signin-page-divider {
  display: flex;
  align-items: center;
  margin: var(--casa-espacio-6) 0;
  color: var(--ink-hushed);
  font-family: inherit;
  font-size: var(--casa-letra-etiqueta);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.signin-page-divider::before,
.signin-page-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border);
}
.signin-page-divider > span {
  padding: 0 var(--casa-espacio-3-5);
}

.signin-page-input { margin-bottom: var(--casa-espacio-3); }

.signin-page-forgot {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 var(--casa-espacio-4);
  font-size: var(--casa-letra-control);
}

.signin-page-error {
  margin: 0 0 var(--casa-espacio-3);
  padding: var(--casa-espacio-2-5) var(--casa-espacio-3);
  background: var(--casa-fondo);
  border: 1px solid var(--casa-error);
  border-left: 3px solid var(--casa-error);
  border-radius: var(--casa-radio-tarjeta);
  color: var(--casa-error);
  font-size: var(--casa-letra-control);
  font-weight: 400;
}

.signin-page-submit {
  margin-top: var(--casa-espacio-1);
  height: 46px !important;
  background-color: var(--ii-accent) !important;
  color: var(--casa-texto-inverso) !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  border-radius: var(--casa-radio-tarjeta) !important;
  box-shadow: none !important;
}
.signin-page-submit:hover { background-color: var(--ii-accent-2) !important; }
.signin-page-submit:disabled {
  background-color: var(--casa-fondo-app) !important;
  color: var(--casa-texto-tenue) !important;
}

.signin-page-footer {
  margin-top: var(--casa-espacio-8);
  font-family: inherit;
  font-size: var(--casa-letra-texto);
  font-weight: 400;
  color: var(--ink-hushed);
  line-height: 1.5;
}
.signin-page-link {
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 400;
}
.signin-page-link--muted {
  color: var(--ink-hushed);
  font-weight: 400;
}
.signin-page-link--muted:hover { color: var(--ink); }
.signin-page-arrow {
  display: inline-block;
  margin-left: var(--casa-espacio-0-5);
  transform: translateY(-1px);
}

.signin-page-input :deep(.v-input__slot) {
  min-height: 46px !important;
  border-radius: var(--casa-radio-tarjeta) !important;
}
.signin-page-input :deep(fieldset) {
  border-color: var(--border) !important;
  border-width: 1px !important;
}
.signin-page-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: var(--ii-accent) !important;
  border-width: 1px !important;
}
.signin-page-input :deep(.v-label) {
  color: var(--ink-hushed) !important;
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
}
.signin-page-input :deep(input) {
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  padding: var(--casa-espacio-2-5) var(--casa-espacio-1) !important;
  color: var(--ink) !important;
}
.signin-page-input :deep(.v-icon) {
  color: var(--ink-hushed) !important;
}

@media (max-width: 480px) {
  .signin-page-heading { font-size: 36px; }
  .signin-page-main { padding: var(--casa-espacio-6) var(--casa-espacio-5); }
}
</style>
